import axios from 'axios';
import { BACKEND_URL } from './api';

const API_HOST = 'instagram120.p.rapidapi.com';

// Bypass Instagram image CORS/hotlink protection
export function getProxiedImage(url) {
  if (!url) return '';
  if (url.includes('images.weserv.nl')) return url;
  return `https://images.weserv.nl/?url=${encodeURIComponent(url)}&default=404`;
}

let cachedKey = '';

export async function getRapidApiKey() {
  if (cachedKey) return cachedKey;
  
  const localKey = localStorage.getItem('rapidapi_key');
  if (localKey) {
    cachedKey = localKey;
    return cachedKey;
  }

  try {
    const response = await axios.get(`${BACKEND_URL}/api/influencers/config/rapidapi`);
    if (response.data && response.data.key) {
      cachedKey = response.data.key;
      return cachedKey;
    }
  } catch (error) {
    console.error('Failed to fetch RapidAPI key from backend:', error);
  }
  
  return '';
}

async function apiFetch(path, body) {
  const key = await getRapidApiKey();
  if (!key) throw new Error('NO_API_KEY');

  const res = await fetch(`https://${API_HOST}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-RapidAPI-Key': key,
      'X-RapidAPI-Host': API_HOST,
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    if (res.status === 403 || res.status === 401) throw new Error('INVALID_API_KEY');
    if (res.status === 429) throw new Error('RATE_LIMIT');
    if (res.status === 404 || res.status === 422) throw new Error('USER_NOT_FOUND');
    throw new Error('API_ERROR_' + res.status);
  }

  const json = await res.json();
  if (json && (json.error || json.status === 'fail' || json.status === 'error')) {
    const msg = (json.error || json.message || '').toLowerCase();
    if (msg.includes('not found') || msg.includes('no user') || msg.includes("doesn't exist") || msg.includes('invalid')) {
      throw new Error('USER_NOT_FOUND');
    }
    if (msg.includes('rate') || msg.includes('limit') || msg.includes('too many')) throw new Error('RATE_LIMIT');
    throw new Error('API_BODY_ERROR: ' + (json.error || json.message || JSON.stringify(json)));
  }
  if (json && json.data === null && json.status === 'ok') throw new Error('USER_NOT_FOUND');
  return json;
}

async function fetchProfile(username) {
  let lastErr;
  for (const ep of ['/api/instagram/userInfo', '/api/instagram/profile']) {
    try {
      const raw = await apiFetch(ep, { username });
      const profile = parseProfile(raw, username);
      if (profile.username || profile.fullName || profile.followers > 0) return profile;
    } catch (e) {
      lastErr = e;
      if (e.message === 'NO_API_KEY' || e.message === 'INVALID_API_KEY' || e.message === 'RATE_LIMIT') throw e;
    }
  }
  throw lastErr || new Error('USER_NOT_FOUND');
}

function parseProfile(raw, username) {
  let d = raw;
  if (raw && raw.result) {
    if (Array.isArray(raw.result) && raw.result[0] && raw.result[0].user) {
      d = raw.result[0].user;
    } else {
      d = raw.result;
    }
  }
  return {
    fullName: d.full_name || d.name || '',
    username: d.username || username || '',
    biography: d.biography || d.bio || '',
    followers: d.follower_count || d.followers || (d.edge_followed_by && d.edge_followed_by.count) || 0,
    following: d.following_count || d.following || (d.edge_follow && d.edge_follow.count) || 0,
    totalPosts: d.media_count || d.posts_count || (d.edge_owner_to_timeline_media && d.edge_owner_to_timeline_media.count) || 0,
    isVerified: d.is_verified || d.verified || false,
    profilePicUrl: d.profile_pic_url_hd || d.profile_pic_url || '',
    externalUrl: d.external_url || '',
    isPrivate: d.is_private || false,
    accountType: d.account_type || 'personal',
  };
}

async function fetchPosts(username) {
  let postsRaw, reelsRaw;
  try {
    const results = await Promise.allSettled([
      apiFetch('/api/instagram/posts', { username }),
      apiFetch('/api/instagram/reels', { username })
    ]);
    postsRaw = results[0].status === 'fulfilled' ? results[0].value : null;
    reelsRaw = results[1].status === 'fulfilled' ? results[1].value : null;
  } catch (e) {
    console.error('Fetch error:', e);
  }

  const postItems = extractItems(postsRaw);
  const reelItems = extractItems(reelsRaw);

  const parsedPosts = postItems.map(parsePost);
  const parsedReels = reelItems.map(parsePost);

  const postMap = {};
  parsedPosts.forEach(p => { postMap[p.id] = p; });

  parsedReels.forEach(r => {
    if (postMap[r.id]) {
      if (r.reelViews > postMap[r.id].reelViews) {
        postMap[r.id].reelViews = r.reelViews;
      }
    } else {
      postMap[r.id] = r;
    }
  });

  const combined = Object.values(postMap).sort((a, b) => new Date(b.date) - new Date(a.date));
  const finalPosts = combined.slice(0, 12);
  return { posts: finalPosts, computed: computeStats(finalPosts) };
}

function extractItems(raw) {
  let items = [];
  if (raw && raw.result) {
    if (Array.isArray(raw.result)) {
      items = raw.result;
    } else if (raw.result.edges) {
      items = raw.result.edges;
    }
  }
  return items.map(e => {
    const item = e.node || e;
    return item.media || item;
  });
}

function parsePost(item) {
  let format = 'Image';
  const isVideo = item.is_video || item.media_type === 2 || item.product_type === 'clips' || item._isReel;
  if (isVideo) format = 'Reel';
  else if (item.media_type === 8 || item.__typename === 'GraphSidecar' || item.carousel_media) format = 'Carousel';
  else if (item.product_type === 'story') format = 'Story';

  const likes = item.like_count || (item.edge_media_preview_like && item.edge_media_preview_like.count) || 0;
  const comments = item.comment_count || (item.edge_media_to_comment && item.edge_media_to_comment.count) || 0;

  const reelViews = item.play_count ||
    item.video_view_count ||
    item.view_count ||
    item.video_play_count ||
    (item.edge_media_preview_video_view && item.edge_media_preview_video_view.count) ||
    (item.edge_video_play_count && item.edge_video_play_count.count) ||
    (item.metrics && (item.metrics.play_count || item.metrics.video_view_count || item.metrics.view_count)) || 0;

  const takenAt = item.taken_at ? new Date(item.taken_at * 1000)
    : item.timestamp ? new Date(item.timestamp * 1000) : null;

  const caption = (item.caption && item.caption.text) ||
    (item.edge_media_to_caption && item.edge_media_to_caption.edges &&
      item.edge_media_to_caption.edges[0] && item.edge_media_to_caption.edges[0].node &&
      item.edge_media_to_caption.edges[0].node.text) || '';
  const hashtags = (caption.match(/#[\w]+/g) || []).map(h => h.toLowerCase());
  const mentions = (caption.match(/@[\w.]+/g) || []).map(m => m.toLowerCase());
  const code = item.code || item.shortcode || item.pk || '';
  return {
    id: item.pk || item.id || ('' + Math.random()),
    url: code ? 'https://www.instagram.com/p/' + code + '/' : '',
    format,
    date: takenAt ? takenAt.toISOString() : null,
    caption, hashtags, mentions,
    likes, comments, reelViews,
    thumbnailUrl: item.thumbnail_url || (item.image_versions2 && item.image_versions2.candidates && item.image_versions2.candidates[0] && item.image_versions2.candidates[0].url) || '',
  };
}

function computeStats(posts) {
  if (!posts.length) return null;
  const reelPosts = posts.filter(p => p.format === 'Reel');
  const allViews = reelPosts.map(p => p.reelViews).filter(v => v > 0);
  const totalLikes = posts.reduce((a, p) => a + p.likes, 0);
  const totalComm = posts.reduce((a, p) => a + p.comments, 0);

  const datedPosts = posts.filter(p => p.date).sort((a, b) => new Date(a.date) - new Date(b.date));
  let postsPerWeek = 0, reelsPerWeek = 0, avgTimeBetween = null;
  if (datedPosts.length >= 2) {
    const first = new Date(datedPosts[0].date), last = new Date(datedPosts[datedPosts.length - 1].date);
    const weeks = Math.max((last - first) / (1000 * 60 * 60 * 24 * 7), 1);
    postsPerWeek = parseFloat((datedPosts.length / weeks).toFixed(2));
    const diffs = [];
    for (let i = 1; i < datedPosts.length; i++) diffs.push(new Date(datedPosts[i].date) - new Date(datedPosts[i - 1].date));
    avgTimeBetween = (diffs.reduce((a, b) => a + b, 0) / diffs.length / 3600000).toFixed(1) + 'h';
    const rDated = reelPosts.filter(p => p.date).sort((a, b) => new Date(a.date) - new Date(b.date));
    if (rDated.length >= 2) { 
      const rW = Math.max((new Date(rDated[rDated.length - 1].date) - new Date(rDated[0].date)) / (1000 * 60 * 60 * 24 * 7), 1); 
      reelsPerWeek = parseFloat((rDated.length / rW).toFixed(2)); 
    }
  }

  const sorted = allViews.slice().sort((a, b) => a - b);
  const avgViews = allViews.length ? Math.round(allViews.reduce((a, b) => a + b, 0) / allViews.length) : 0;
  const medViews = sorted.length ? sorted[Math.floor(sorted.length / 2)] : 0;
  const maxViews = sorted.length ? sorted[sorted.length - 1] : 0;
  const minViews = sorted.length ? sorted[0] : 0;
  const viralPosts = reelPosts.filter(p => p.reelViews > avgViews * 2);

  const fmtCounts = { Reel: 0, Carousel: 0, Image: 0, Story: 0 };
  posts.forEach(p => { fmtCounts[p.format] = (fmtCounts[p.format] || 0) + 1; });

  const CKW = {
    Fashion: ['fashion', 'style', 'ootd', 'outfit', 'clothes', 'wear', 'brand', 'model'],
    Travel: ['travel', 'trip', 'explore', 'wanderlust', 'vacation', 'tour', 'destination'],
    Lifestyle: ['life', 'lifestyle', 'daily', 'routine', 'morning', 'wellness', 'selfcare'],
    Beauty: ['beauty', 'makeup', 'skincare', 'glow', 'cosmetics'],
    Fitness: ['fitness', 'gym', 'workout', 'health', 'training', 'gains', 'muscle'],
    Food: ['food', 'eat', 'recipe', 'cooking', 'chef', 'restaurant', 'foodie'],
  };
  const combined = posts.map(p => p.caption.toLowerCase()).join(' ') + ' ' + posts.reduce((a, p) => a.concat(p.hashtags), []).join(' ');
  const catScores = {}; const catPct = {};
  Object.keys(CKW).forEach(cat => {
    catScores[cat] = CKW[cat].filter(k => combined.includes(k)).length;
    const matched = posts.filter(p => CKW[cat].some(k => p.caption.toLowerCase().includes(k) || p.hashtags.some(h => h.includes(k))));
    catPct[cat] = posts.length ? Math.round(matched.length / posts.length * 100) : 0;
  });
  const sortedCats = Object.entries(catScores).sort((a, b) => b[1] - a[1]);
  const allHashtags = posts.reduce((a, p) => a.concat(p.hashtags), []);
  const hFreq = allHashtags.reduce((acc, h) => { acc[h] = (acc[h] || 0) + 1; return acc; }, {});

  return {
    totalLikes, totalComments: totalComm,
    avgLikes: Math.round(totalLikes / posts.length), avgComments: Math.round(totalComm / posts.length),
    avgReelViews: avgViews, medianViews: medViews, maxViews: maxViews, minViews: minViews,
    viralPostsCount: viralPosts.length,
    viralPostRatio: reelPosts.length ? parseFloat((viralPosts.length / reelPosts.length * 100).toFixed(1)) : 0,
    postsPerWeek, reelsPerWeek, avgTimeBetween,
    formatCounts: fmtCounts,
    primaryCategory: sortedCats[0] ? sortedCats[0][0] : 'Lifestyle',
    secondaryCategory: sortedCats[1] ? sortedCats[1][0] : '',
    categoryPercents: catPct,
    topHashtags: Object.entries(hFreq).sort((a, b) => b[1] - a[1]).slice(0, 10),
  };
}

export async function fetchPostInfo(identifier) {
  let shortcode = identifier;
  
  if (identifier.includes('instagram.com/')) {
    const parts = identifier.split('/').filter(p => p !== '');
    const pidx = parts.indexOf('p') > -1 ? parts.indexOf('p') : parts.indexOf('reel');
    if (pidx > -1 && parts[pidx + 1]) {
      shortcode = parts[pidx + 1].split('?')[0];
    }
  }

  let lastErr;
  for (const ep of ['/api/instagram/postInfo', '/api/instagram/mediaInfo']) {
    try {
      const params = ep.includes('mediaInfo') ? { shortcode } : { code: shortcode };
      const raw = await apiFetch(ep, params);
      const item = (raw && raw.result) ? raw.result : raw;
      if (item && (item.pk || item.id || item.code)) {
        return parsePost(item);
      }
    } catch (err) {
      lastErr = err;
      if (err.message === 'INVALID_API_KEY' || err.message === 'RATE_LIMIT') throw err;
    }
  }

  throw lastErr || new Error('POST_NOT_FOUND');
}

export async function fetchInfluencer(username) {
  const results = await Promise.all([fetchProfile(username), fetchPosts(username)]);
  const profileData = results[0], postsData = results[1];
  const posts = postsData.posts, computed = postsData.computed;
  const followers = profileData.followers;
  const engagementRate = (followers && computed) ? ((computed.avgLikes + computed.avgComments) / followers * 100).toFixed(2) : '0.00';
  const viewRate = (followers && computed && computed.avgReelViews) ? (computed.avgReelViews / followers * 100).toFixed(2) : '0.00';
  const postsWithER = posts.map(p => {
    return Object.assign({}, p, {
      engagementRate: followers ? ((p.likes + p.comments) / followers * 100).toFixed(2) : '0.00',
      viralScore: (computed && computed.avgReelViews && p.reelViews) ? (p.reelViews / (computed.avgReelViews || 1)).toFixed(2) : null,
    });
  });
  return { profile: profileData, posts: postsWithER, computed: Object.assign({}, computed, { engagementRate, viewRate }), lastSynced: new Date().toISOString(), history: [] };
}

const API_BASE_URL = `${BACKEND_URL}/api`;

export async function getSavedTrackers() {
  try {
    const response = await axios.get(`${API_BASE_URL}/instagram-tracker`);
    return response.data.influencers || {};
  } catch (error) {
    console.error('Failed to get trackers', error);
    return {};
  }
}

export async function saveTracker(username, data) {
  try {
    await axios.post(`${API_BASE_URL}/instagram-tracker/${username}`, data);
    return true;
  } catch (error) {
    console.error('Failed to save tracker', error);
    throw error;
  }
}

export async function deleteTracker(username) {
  try {
    await axios.delete(`${API_BASE_URL}/instagram-tracker/${username}`);
    return true;
  } catch (error) {
    console.error('Failed to delete tracker', error);
    throw error;
  }
}
